import { GenericSuccessDialog } from '@components/display/dialogs';
import { RadioToggleButton } from '@components/utility/buttons';
import { DirtyFormikListener } from '@hooks/useDirtyFormik';
import { useScrollIntoError } from '@hooks/useScrollIntoError';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { Box, Divider, Grid2, Typography } from '@mui/material';
import { VRMFormDialog } from '@rla/components/dialogs';
import { RLAPinForm } from '@rla/routes/RLAForm/RLAPinForm';
import { RLAVRMForm } from '@rla/routes/RLAForm/RLAVRMForm';
import { RLAPinPukSchema, RLAVRMSchema } from '@rla/schemas';
import { useCreatePinPukRecoveryRequestMutation, useCreateVrmRecoveryRequestMutation } from '@rla/services';
import { PinPukRecoveryForm, RLAFormMode, VRMRecoveryForm } from '@rla/types';
import { handleError } from '@utils/common';
import { FormikContext, FormikHelpers, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

type CreateRecoveryRequestMutation =
    | typeof useCreatePinPukRecoveryRequestMutation
    | typeof useCreateVrmRecoveryRequestMutation;
type CreateRecoveryRequestTrigger = ReturnType<CreateRecoveryRequestMutation>[0];

const trimTextFields = <T extends object>(values: T): T =>
    Object.fromEntries(
        Object.entries(values).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
    ) as T;

const handleSubmit = async ({
    values,
    createRecoveryRequest,
    helpers: { resetForm },
    intl,
    setShowSuccessDialog,
}: {
    values: PinPukRecoveryForm | VRMRecoveryForm;
    createRecoveryRequest: CreateRecoveryRequestTrigger;
    helpers: FormikHelpers<PinPukRecoveryForm> | FormikHelpers<VRMRecoveryForm>;
    setShowSuccessDialog: (show: boolean) => void;
    intl: IntlShape;
}) => {
    try {
        const formData = new FormData();
        Object.entries(trimTextFields(values)).forEach(([key, value]) => {
            if (typeof value === 'boolean') return;

            if (Array.isArray(value)) {
                value.forEach((item) => formData.append(key, item.file));
            } else {
                formData.append(key, value);
            }
        });
        await createRecoveryRequest(formData).unwrap();
        resetForm();
        setShowSuccessDialog(true);
    } catch (error) {
        handleError(
            intl.formatMessage({
                id: 'rlaForm.submitError',
                defaultMessage: 'An error occurred while submitting the form.',
            }),
            error
        );
    }
};

export const RLAForm = () => {
    const [formMode, setFormMode] = useState<RLAFormMode>(RLAFormMode.PIN_PUK_RECOVERY);
    const [vrmFormDialogOpen, setVRMFormDialogOpen] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const intl = useIntl();

    const [createPinPukRecoveryRequest] = useCreatePinPukRecoveryRequestMutation();
    const pinPukRecoverySchema = useMemo(() => RLAPinPukSchema(intl), [intl]);
    const pinPukRecoveryFormik = useFormik<PinPukRecoveryForm>({
        enableReinitialize: true,
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            distributor_name: '',
            product_serial_number: '',
            documents: [],
            comment: '',
            confirm_intent: false,
            confirm_truth: false,
        },
        validationSchema: pinPukRecoverySchema,
        onSubmit: async (values, helpers) => {
            await handleSubmit({
                values,
                helpers,
                createRecoveryRequest: createPinPukRecoveryRequest,
                intl,
                setShowSuccessDialog,
            });
        },
    });

    const [createVrmRecoveryRequest] = useCreateVrmRecoveryRequestMutation();
    const vrmRecoverySchema = useMemo(() => RLAVRMSchema(intl), [intl]);
    const vrmRecoveryFormik = useFormik<VRMRecoveryForm>({
        enableReinitialize: true,
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            distributor_name: '',
            gx_serial_number: '',
            vrm_portal_id: '',
            type_of_request: 'new_owner_request',
            documents: [],
            comment: '',
            confirm_intent: false,
            confirm_truth: false,
        },
        validationSchema: vrmRecoverySchema,
        onSubmit: async (values, helpers) => {
            await handleSubmit({
                values,
                helpers,
                createRecoveryRequest: createVrmRecoveryRequest,
                intl,
                setShowSuccessDialog,
            });
        },
    });

    const { scrollIntoError } = useScrollIntoError({
        formik: formMode === RLAFormMode.PIN_PUK_RECOVERY ? pinPukRecoveryFormik : (vrmRecoveryFormik as any),
    });

    useEffect(() => {
        scrollIntoError();
    }, [formMode]);

    return (
        <>
            <title>Recover Lost Access Form</title>
            <Box py="24px" px={{ xs: '24px', lg: '62px' }}>
                <Box
                    p="24px"
                    border={1}
                    borderRadius="5px"
                    borderColor="rgba(0, 0, 0, 0.16)"
                    bgcolor="background.paper"
                >
                    <Typography fontFamily="museo-sans" fontSize={26} letterSpacing={-1.075} py="26px">
                        <FormattedMessage id="rlaForm.title" defaultMessage="Recover Lost Access Form" />
                    </Typography>
                    <Divider />
                    <form
                        noValidate
                        onSubmit={
                            formMode === RLAFormMode.PIN_PUK_RECOVERY
                                ? pinPukRecoveryFormik.handleSubmit
                                : vrmRecoveryFormik.handleSubmit
                        }
                    >
                        <Grid2 container py="24px" spacing="24px">
                            <Grid2 size={{ xs: 12, lg: 6 }}>
                                <RadioToggleButton
                                    value={RLAFormMode.PIN_PUK_RECOVERY}
                                    fullWidth
                                    sx={{ height: '100%' }}
                                    Icon={LockOpenOutlinedIcon}
                                    title={
                                        <FormattedMessage
                                            id="rlaForm.pinRecovery.title"
                                            defaultMessage="PIN/PUK Code Recovery Request"
                                        />
                                    }
                                    subtitle={
                                        <FormattedMessage
                                            id="rlaForm.pinRecovery.subtitle"
                                            defaultMessage="Recover your device's PIN or PUK code"
                                        />
                                    }
                                    selected={formMode === RLAFormMode.PIN_PUK_RECOVERY}
                                    onChange={() => setFormMode(RLAFormMode.PIN_PUK_RECOVERY)}
                                    disabled={pinPukRecoveryFormik.isSubmitting || vrmRecoveryFormik.isSubmitting}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 6 }}>
                                <RadioToggleButton
                                    value={RLAFormMode.VRM_ADMIN_ACCESS}
                                    fullWidth
                                    sx={{ height: '100%' }}
                                    Icon={LockOpenOutlinedIcon}
                                    title={
                                        <FormattedMessage
                                            id="rlaForm.vrmAdminAccess.title"
                                            defaultMessage="VRM Access & Change of Ownership Request"
                                        />
                                    }
                                    subtitle={
                                        <FormattedMessage
                                            id="rlaForm.vrmAdminAccess.subtitle"
                                            defaultMessage="Request admin access to VRM"
                                        />
                                    }
                                    selected={formMode === RLAFormMode.VRM_ADMIN_ACCESS}
                                    onChange={() => setVRMFormDialogOpen(true)}
                                    disabled={pinPukRecoveryFormik.isSubmitting || vrmRecoveryFormik.isSubmitting}
                                />
                            </Grid2>

                            {formMode === RLAFormMode.PIN_PUK_RECOVERY ? (
                                <FormikContext.Provider value={pinPukRecoveryFormik}>
                                    <DirtyFormikListener />
                                    <RLAPinForm />
                                </FormikContext.Provider>
                            ) : (
                                <FormikContext.Provider value={vrmRecoveryFormik}>
                                    <DirtyFormikListener />
                                    <RLAVRMForm />
                                </FormikContext.Provider>
                            )}
                            <GenericSuccessDialog
                                open={showSuccessDialog}
                                onClose={() => setShowSuccessDialog(false)}
                                classes={{
                                    paper: 'max-w-[450px]',
                                }}
                                contentProps={{
                                    title: intl.formatMessage({
                                        id: 'dialog.success.title',
                                        defaultMessage: 'Success!',
                                    }),
                                    description: intl.formatMessage({
                                        id: 'dialog.success.description',
                                        defaultMessage: 'Recover Lost Access Form was submitted successfully.',
                                    }),
                                }}
                            />
                        </Grid2>
                    </form>
                </Box>
            </Box>
            <VRMFormDialog
                open={vrmFormDialogOpen}
                onClose={() => setVRMFormDialogOpen(false)}
                onSuccess={() => setFormMode(RLAFormMode.VRM_ADMIN_ACCESS)}
            />
        </>
    );
};
